import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import { NotificationApiService } from '../../../service/notificationApiService';
import { AuthApiService } from '../../../service/authApiService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import {
  FaBars, FaBell, FaChevronDown,
  FaUser, FaCog, FaSignOutAlt
} from 'react-icons/fa';
import GlobalSearch from './GlobalSearch';
import './adminHeader.css';

// Page title mapping
const pageTitles = {
  '/admin/dashboard': 'Dashboard',
  '/admin/user-management': 'User Management',
  '/admin/user-statistics': 'User Statistics',
  '/admin/referral-management': 'Referral Management',
  '/admin/search-log': 'Search Log',
  '/admin/internal-users': 'Internal Users',
  '/admin/roles': 'Role Management',
  '/admin/products/list': 'Product List',
  '/admin/products/add': 'Add Product',
  '/admin/product-drafts': 'Product Drafts',
  '/admin/categories': 'Categories',
  '/admin/tags': 'Tags',
  '/admin/featured-categories': 'Featured Categories',
  '/admin/category-approval': 'Category Approval',
  '/admin/alibaba-product': 'Global Product Search',
  '/admin/sku-upload': 'SKU Upload',
  '/admin/order-management': 'Order Management',
  '/admin/cross-border-orders': 'Cross-Border Orders',
  '/admin/failed-orders': 'Failed Orders',
  '/admin/transactions': 'Transactions',
  '/admin/subscription/list': 'Subscription List',
  '/admin/subscription/add': 'Add Subscription',
  '/admin/membership-management': 'Membership Management',
  '/admin/pricing-algorithms': 'Pricing Algorithms',
  '/admin/warehouse-management': 'Warehouse Management',
  '/admin/logistics': 'Logistics',
  '/admin/supplier-data-info': 'Supplier Data Info',
  '/admin/ticket-management': 'Ticket Management',
  '/admin/inquiries': 'Inquiries',
  '/admin/cron-logs': 'Cron Logs',
  '/admin/ai-management': 'AI Management',
  '/admin/restricted-words': 'Restricted Words'
};

export default function AdminHeader({ onMenuClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const message = useMessage();
  
  const [userData, setUserData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (auth && auth.isLoggedIn) {
      // Fetch user profile to get first name and last name
      AuthApiService.getProfile()
        .then((res) => {
          if (res?.status?.code === 200 && res?.data) {
            const firstName = res.data.firstName || '';
            const lastName = res.data.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim() || 'Admin User';
            setUserData({
              name: fullName,
              role: auth.userType || res.data.role || 'admin',
              email: res.data.email || '',
            });
          } else {
            setUserData({
              name: 'Admin User',
              role: auth.userType || 'admin',
              email: '',
            });
          }
        })
        .catch(() => {
          setUserData({
            name: 'Admin User',
            role: auth.userType || 'admin',
            email: '',
          });
        });
      fetchNotifications();
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const countRes = await NotificationApiService.getUnseenCount();
      if (countRes?.status?.code === 200) {
        setUnseenCount(countRes?.data?.count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const openNotifications = async () => {
    setShowNotifications(!showNotifications);
    setShowProfileMenu(false);
    
    if (!showNotifications) {
      try {
        const res = await NotificationApiService.getNotifications('?page=1&limit=10');
        if (res?.status?.code === 200) {
          setNotifications(res?.data?.notifications || []);
          await NotificationApiService.markAllSeen();
          setUnseenCount(0);
        }
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    // Check for exact match first
    if (pageTitles[path]) return pageTitles[path];
    // Check for partial matches (for dynamic routes like /admin/ai-management/models/123)
    for (const [route, title] of Object.entries(pageTitles)) {
      if (path.startsWith(route + '/') || path === route) return title;
    }
    return 'Admin Panel';
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);
    
    return parts.map((part, index) => {
      const url = '/' + parts.slice(0, index + 1).join('/');
      const label = part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ');
      return { label, url, isLast: index === parts.length - 1 };
    });
  };

  const getInitials = (name) => {
    if (!name) return 'A';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <header className="kiro-admin-header">
      {/* Left Section */}
      <div className="kiro-header-left">
        <button className="kiro-header-menu-btn" onClick={onMenuClick}>
          <FaBars />
        </button>
        
        <h1 className="kiro-header-title">{getPageTitle()}</h1>
      </div>

      {/* Right Section */}
      <div className="kiro-header-right">
        {/* Global Search */}
        <GlobalSearch />

        {/* Action Buttons */}
        <div className="kiro-header-actions">
          {/* Notifications */}
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button 
              className="kiro-header-action-btn"
              onClick={openNotifications}
            >
              <FaBell />
              {unseenCount > 0 && (
                <span className="kiro-header-action-badge">{unseenCount > 99 ? '99+' : unseenCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className="kiro-notification-dropdown">
                <div className="kiro-notification-header">
                  <span className="kiro-notification-title">Notifications</span>
                  <button className="kiro-notification-mark-read">Mark all read</button>
                </div>
                
                <div className="kiro-notification-list">
                  {notifications.length === 0 ? (
                    <div className="kiro-notification-empty">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.map((notif, index) => (
                      <div 
                        key={index} 
                        className={`kiro-notification-item ${!notif.seen ? 'kiro-unread' : ''}`}
                        onClick={() => {
                          if (notif.link) navigate(notif.link);
                          setShowNotifications(false);
                        }}
                      >
                        <div className="kiro-notification-item-title">{notif.title}</div>
                        <div className="kiro-notification-item-desc">{notif.message}</div>
                        <div className="kiro-notification-item-time">{formatTimeAgo(notif.createdAt)}</div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="kiro-notification-footer">
                  <Link to="/admin/notifications" className="kiro-notification-view-all">
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Profile */}
        <div ref={profileRef} style={{ position: 'relative' }}>
          <div 
            className={`kiro-header-profile ${showProfileMenu ? 'kiro-dropdown-open' : ''}`}
            onClick={() => {
              setShowProfileMenu(!showProfileMenu);
              setShowNotifications(false);
            }}
          >
            <div className="kiro-header-avatar">
              {getInitials(userData?.name)}
            </div>
            <div className="kiro-header-user-info">
              <span className="kiro-header-user-name">{userData?.name}</span>
              <span className="kiro-header-user-role">{userData?.role}</span>
            </div>
            <FaChevronDown className="kiro-header-dropdown-arrow" />
          </div>

          {showProfileMenu && (
            <div className="kiro-profile-dropdown">
              <div className="kiro-profile-dropdown-header">
                <div className="kiro-profile-dropdown-avatar">
                  {getInitials(userData?.name)}
                </div>
                <div className="kiro-profile-dropdown-info">
                  <div className="kiro-profile-dropdown-name">{userData?.name}</div>
                  <div className="kiro-profile-dropdown-email">{userData?.email}</div>
                </div>
              </div>
              <div className="kiro-profile-dropdown-divider" />
              <Link to="/admin/user-management" className="kiro-profile-dropdown-item" onClick={() => setShowProfileMenu(false)}>
                <FaUser className="kiro-profile-dropdown-icon" />
                User Management
              </Link>
              <Link to="/admin/roles" className="kiro-profile-dropdown-item" onClick={() => setShowProfileMenu(false)}>
                <FaCog className="kiro-profile-dropdown-icon" />
                Role Management
              </Link>
              <div className="kiro-profile-dropdown-divider" />
              <button className="kiro-profile-dropdown-item kiro-logout" onClick={handleLogout}>
                <FaSignOutAlt className="kiro-profile-dropdown-icon" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
